import React, { useState } from 'react';
import { Hash, RotateCcw } from 'lucide-react';
import { calcNameNumerology, numberMeanings } from '../lib/numerology';
import { toast } from 'sonner';

const Calculators = () => {
  const [name, setName] = useState('');
  const [system, setSystem] = useState('pythagorean');
  const [result, setResult] = useState(null);

  const submit = (e) => {
    e.preventDefault();
    if (!name.trim()) {
      toast.error('Please enter your name to begin.');
      return;
    }
    const r = calcNameNumerology(name, system);
    if (!r) return;
    setResult(r);
    toast.success(`Your ${system} name number is ${r.single}`);
  };

  const reset = () => { setName(''); setResult(null); };
  const meaning = result ? numberMeanings[result.single] : null;

  return (
    <section id="calculators" className="py-28 bg-[#faf7f0]">
      <div className="max-w-6xl mx-auto px-6 lg:px-10">
        <div className="text-center mb-12">
          <div className="ornament justify-center inline-flex mb-4">
            <span className="section-eyebrow">Sacred Calculator</span>
          </div>
          <h2 className="font-display text-4xl md:text-5xl text-[#1a1714]">
            Decode your <span className="italic font-cormorant gold-text">name number</span>
          </h2>
          <p className="mt-4 max-w-xl mx-auto text-[#4a4035] text-[15px] font-light">
            A complimentary tool — a gentle introduction to the deeper readings offered by Mrs. Pujah Chopraa.
          </p>
        </div>

        <div className="card-luxe p-5 sm:p-8 md:p-10">
          <div className="grid lg:grid-cols-2 gap-8">
            <form onSubmit={submit} className="space-y-6">
              <div>
                <label className="section-eyebrow block mb-3">Your Full Name</label>
                <input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g. Aanya Sharma"
                  className="w-full bg-transparent border-b border-[#c9a961]/50 px-1 py-3 font-display text-2xl focus:outline-none focus:border-[#a8842c] text-[#1a1714] placeholder:text-[#c9a961]/50"
                />
              </div>
              <div>
                <label className="section-eyebrow block mb-3">System</label>
                <div className="flex gap-3">
                  {['pythagorean', 'chaldean'].map((sys) => (
                    <button
                      type="button"
                      key={sys}
                      onClick={() => setSystem(sys)}
                      className={`px-4 py-2 text-[11px] tracking-[0.25em] uppercase border transition-all ${
                        system === sys
                          ? 'bg-[#c9a961]/20 border-[#a8842c] text-[#1a1714]'
                          : 'border-[#c9a961]/40 text-[#6b5e4f]'
                      }`}
                    >
                      {sys}
                    </button>
                  ))}
                </div>
              </div>
              <div className="flex gap-3 pt-2">
                <button type="submit" className="btn-gold">Reveal Number</button>
                <button type="button" onClick={reset} className="btn-outline-gold flex items-center gap-2">
                  <RotateCcw size={14} /> Reset
                </button>
              </div>
            </form>

            <div className="relative bg-gradient-to-br from-[#1a1714] to-[#2b2521] text-[#faf7f0] p-8 min-h-[320px] flex flex-col justify-center overflow-hidden">
              <div className="absolute -top-10 -right-10 w-40 h-40 border border-[#c9a961]/20 rotate-45" />
              <div className="absolute -bottom-10 -left-10 w-32 h-32 border border-[#c9a961]/20 rotate-45" />
              {result ? (
                <div className="relative">
                  <div className="text-[10px] tracking-[0.35em] uppercase text-[#c9a961] mb-2">Your Number</div>
                  <div className="font-display text-[80px] sm:text-[110px] leading-none gold-text">{result.single}</div>
                  <div className="text-xs tracking-[0.25em] uppercase text-[#e8d9a8] mt-1">
                    Total · {result.total}
                  </div>
                  {meaning && (
                    <div className="mt-6 border-t border-[#c9a961]/30 pt-4">
                      <div className="font-display text-xl">{meaning.title}</div>
                      <p className="text-[13px] text-[#d6cdb8] mt-2 leading-relaxed">{meaning.text}</p>
                    </div>
                  )}
                  <div className="mt-5 flex flex-wrap gap-1.5 text-[10px]">
                    {result.breakdown.map((b, i) => (
                      <span key={`${b.letter}-${i}`} className="px-2 py-1 border border-[#c9a961]/30 text-[#e8d9a8]">
                        {b.letter}<span className="text-[#a8842c]">·{b.value}</span>
                      </span>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="relative text-center">
                  <Hash className="mx-auto text-[#c9a961]/50 mb-4" size={36} />
                  <div className="font-display text-3xl">Awaiting your name</div>
                  <p className="text-[#a99c84] text-sm mt-2">Enter your full name to reveal your numerological signature.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Calculators;
